import React, {useEffect, useRef, useState} from 'react';
import {Button, Col, Input, PageHeader, Row, Table,} from 'antd';
import {DeleteOutlined, ReloadOutlined, SearchOutlined} from "@ant-design/icons";
import _ from "lodash";
import {deleteD365ExpenseCode, getD365ExpenseCodes} from "../../api/d365Api";
import {useUser, useUserPermissions} from "../../auth/userHooks";
import {ExpenseCodeButton} from "./ExpenseCodeButton";
import moment from "moment/moment";
import {handleResponse} from "./utils";
import {PREFERRED_DATETIME_FORMAT} from "../../util/time";
import {getPaginationConfig} from "../../util/showAllPagination";
import {useDynamicTableHeight} from "../../util/useDynamicTableHeight";

const ExpenseCodes = () => {
  const [loading, setLoading] = useState(false);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [filteredExpenseCodes, setFilteredExpenseCodes] = useState([]);
  const permissions = useUserPermissions();
  const user = useUser();
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);
  const columns = [
    {
      title: 'Code',
      key: 'ID',
      dataIndex: 'code',
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: i => i ? i : '-'
    },
    {
      title: 'Created By Email',
      dataIndex: 'createdByEmail',
      sorter: (a, b) => a.createdByEmail.localeCompare(b.createdByEmail),
      width: '20%',
      render: i => i ? i : '-'
    },
    {
      title: 'Created At',
      dataIndex: 'CreatedAt',
      sorter: (a, b) => a.CreatedAt.localeCompare(b.CreatedAt),
      width: '225px',
      render: i => i ? moment.utc(i).local().format(PREFERRED_DATETIME_FORMAT) : '-'
    },
    {
      title: 'Action',
      width: '125px',
      key: 'action',
      render: h => (
        <>
          {permissions !== null && permissions.canManageExpenses &&
            <Row>
              <Button
                icon={<DeleteOutlined/>}
                onClick={() => {
                  deleteExpenseCode(h.ID, user.email)
                }}>Remove
              </Button>
            </Row>

          }
        </>
      ),
    },
  ];

  const filterExpenseCodes = (expenseCodes, searchText) => {
    setFilteredExpenseCodes(
      _.chain(expenseCodes)
        .filter(
          (u) =>
            searchText === "" ||
            _.includes(_.toLower(u.code), _.toLower(searchText))
        )
        .value()
    );
  };

  const search = (text) => {
    filterExpenseCodes(expenseCodes, text);
  };

  const loadExpenseCodes = async () => {
    try {
      setLoading(true);
      const expenseCodes = await getD365ExpenseCodes();
      setExpenseCodes(expenseCodes);
      filterExpenseCodes(expenseCodes);
    } catch (e) {
      console.error(`Could not get expense codes`);
    } finally {
      setLoading(false);
    }
  };

  const deleteExpenseCode = async (id, email) => {
    setLoading(true);
    const response = await deleteD365ExpenseCode(id, email);

    handleExpenseCodeResponse(response, "removed");
    setLoading(false);
  };

  const handleExpenseCodeResponse = (response, action) => {
    handleResponse(response, "Expense code", action);
    loadExpenseCodes();
  };

  useEffect(() => {
    loadExpenseCodes();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title="Expense Codes"
            subTitle={`(${filteredExpenseCodes.length} shown)`}
            style={{padding: 0}}
          />
        </Col>
      </Row>

      <Row style={{marginBottom: 10}}>
        <Col span={16} style={{marginRight: 10}}>
          <Input
            style={{maxWidth: 400}}
            placeholder="Search for expense codes by code"
            prefix={<SearchOutlined/>}
            onChange={(e) => search(e.target.value)}
            allowClear
          />
        </Col>
        <Col flex="auto">
          <Button
            style={{ float: 'right'}}
            onClick={() => loadExpenseCodes()}
            loading={loading}
            icon={<ReloadOutlined/>}
          >
            Refresh
          </Button>
          {permissions !== null && permissions.canManageExpenses && (
            <>
              <ExpenseCodeButton
                disabled={loading}
                title={"Create Expense Code"}
                email={user.email}
                onCompleted={handleExpenseCodeResponse}
                style={{marginRight: 10, align: "center", float: 'right'}}
              />
            </>
          )}
        </Col>
      </Row>

      <Table
        ref={tableContainerRef}
        rowKey="ID"
        dataSource={filteredExpenseCodes}
        columns={columns}
        pagination={getPaginationConfig(filteredExpenseCodes.length)}
        size="small"
        loading={loading}
        scroll={{y: tableHeight}}
      />
    </>
  );
};

export {
  ExpenseCodes
};
